import React, { useMemo, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  FaCheckCircle,
  FaCreditCard,
  FaCrown,
  FaGift,
  FaQrcode,
  FaUsers,
} from "react-icons/fa";
import { MdCalculate, MdLiquor, MdOutlineTableBar } from "react-icons/md";
import "./PaymentComponent.scss";

const SANDBOX_MODE = false;

const FULL_PACKAGE_PRICE = 9500;
const FULL_OFFER_PRICE = 6990;
const INSTALLMENT_PRICE = 10000;

const FEATURES = [
  {
    key: "digitalInvite",
    title: "Digital Invitation",
    description: "Personalized digital invitation website for your wedding.",
    normalPrice: 4000,
    offerPrice: 2990,
    icon: <FaGift />,
  },
  {
    key: "guestRsvp",
    title: "Guest RSVPs and Liquor Counts",
    description: "Collect RSVP responses and manage liquor counts easily.",
    normalPrice: 3500,
    offerPrice: 2510,
    icon: <MdLiquor />,
  },
  {
    key: "seatingArrangement",
    title: "Seating Arrangement",
    description: "Plan guest tables and seating arrangements.",
    normalPrice: 2000,
    offerPrice: 1490,
    icon: <MdOutlineTableBar />,
  },
];

const FREE_BONUSES = [
  { title: "Budget Management System", icon: <MdCalculate /> },
  { title: "QR Code for Digital Invitation", icon: <FaQrcode /> },
  { title: "Guest Table Search", icon: <FaUsers /> },
  { title: "Final Guest List Adding Support", icon: <FaCheckCircle /> },
];

const INSTALLMENT_MONTHS = [3, 6, 12];

const PaymentComponent = () => {
  const { slug } = useParams();
  const { weddingData } = useOutletContext();

  const [isPaying, setIsPaying] = useState(false);
  const [paymentType, setPaymentType] = useState("card");
  const [installmentMonths, setInstallmentMonths] = useState(3);

  const [selectedFeatures, setSelectedFeatures] = useState({
    digitalInvite: true,
    guestRsvp: false,
    seatingArrangement: false,
  });

  const isPremium = weddingData?.plan === "premium";

  const isInstallment = paymentType === "installment";

  const selectedFeatureList = useMemo(() => {
    if (isInstallment) return FEATURES;
    return FEATURES.filter((feature) => selectedFeatures[feature.key]);
  }, [isInstallment, selectedFeatures]);

  const totalNormalPrice = useMemo(() => {
    if (isInstallment) return INSTALLMENT_PRICE;
    return selectedFeatureList.reduce((sum, item) => sum + item.normalPrice, 0);
  }, [isInstallment, selectedFeatureList]);

  const totalPayable = useMemo(() => {
    if (isInstallment) return INSTALLMENT_PRICE;
    return selectedFeatureList.reduce((sum, item) => sum + item.offerPrice, 0);
  }, [isInstallment, selectedFeatureList]);

  const isFullPackage =
    selectedFeatures.digitalInvite &&
    selectedFeatures.guestRsvp &&
    selectedFeatures.seatingArrangement;

  const handleFeatureToggle = (featureKey) => {
    if (isInstallment) return;

    setSelectedFeatures((prev) => {
      if (featureKey === "digitalInvite") {
        return prev;
      }

      if (featureKey === "guestRsvp") {
        const nextGuestRsvp = !prev.guestRsvp;

        return {
          ...prev,
          guestRsvp: nextGuestRsvp,
          seatingArrangement: nextGuestRsvp ? prev.seatingArrangement : false,
        };
      }

      if (featureKey === "seatingArrangement") {
        if (!prev.guestRsvp) {
          alert("Please select Guest RSVPs and Liquor Counts first.");
          return prev;
        }

        return {
          ...prev,
          seatingArrangement: !prev.seatingArrangement,
        };
      }

      return prev;
    });
  };

  const handlePaymentTypeChange = (type) => {
    setPaymentType(type);

    if (type === "installment") {
      setSelectedFeatures({
        digitalInvite: true,
        guestRsvp: true,
        seatingArrangement: true,
      });
      return;
    }

    setSelectedFeatures({
      digitalInvite: true,
      guestRsvp: false,
      seatingArrangement: false,
    });
  };

  const handlePayNow = async () => {
    try {
      if (isPremium) {
        alert("Your account is already upgraded to Premium.");
        return;
      }

      setIsPaying(true);

      if (!window.payhere) {
        alert("PayHere script is not loaded. Please check public/index.html.");
        setIsPaying(false);
        return;
      }

      const functions = getFunctions(undefined, "us-central1");
      const createPayHerePayment = httpsCallable(
        functions,
        "createPayHerePayment"
      );

      const selectedFeatureKeys = selectedFeatureList.map((feature) => feature.key);

      const result = await createPayHerePayment({
        slug,
        amount: totalPayable,
        paymentType,
        installmentMonths: isInstallment ? installmentMonths : null,
        selectedFeatures: selectedFeatureKeys,
        isFullPackage: isInstallment || isFullPackage,
      });

      const payment = {
        sandbox: SANDBOX_MODE,
        merchant_id: result.data.merchantId,

        return_url: `${window.location.origin}/sites/${slug}/billing-payments`,
        cancel_url: `${window.location.origin}/sites/${slug}/billing-payments`,
        notify_url:
          "https://us-central1-planmyguest.cloudfunctions.net/payhereNotify",

        order_id: result.data.orderId,
        items:
          isInstallment
            ? `Plan My Guest Full Package - ${installmentMonths} Months Installment`
            : isFullPackage
            ? "Plan My Guest Full Package Offer"
            : selectedFeatureList.map((item) => item.title).join(", "),

        amount: result.data.amount,
        currency: result.data.currency,
        hash: result.data.hash,

        first_name: weddingData?.bride_name || "Customer",
        last_name: weddingData?.groom_name || "",
        email: weddingData?.email || "customer@example.com",
        phone: weddingData?.phone || "0770000000",
        address: "Sri Lanka",
        city: "Colombo",
        country: "Sri Lanka",
      };

      window.payhere.onCompleted = function onCompleted(orderId) {
        console.log("Payment completed. Order ID:", orderId);
        alert("Payment completed successfully. Your account will update shortly.");
        window.location.reload();
      };

      window.payhere.onDismissed = function onDismissed() {
        setIsPaying(false);
      };

      window.payhere.onError = function onError(error) {
        console.error("PayHere error:", error);
        alert("Payment failed. Please try again.");
        setIsPaying(false);
      };

      window.payhere.startPayment(payment);
    } catch (error) {
      console.error("Payment initialization failed:", error);
      alert(error?.message || "Failed to initialize payment.");
      setIsPaying(false);
    }
  };

  if (isPremium) {
    return (
      <section className="paymentPage" aria-label="Billing and payments">
        <div className="paymentPage__hero">
          <p className="paymentPage__eyebrow">Billing & Payments</p>
          <h1 className="paymentPage__title">Premium Activated</h1>
          <p className="paymentPage__subtitle">
            Your Plan My Guest premium package is active. No further payment is required.
          </p>
        </div>

        <div className="paymentPremiumCard">
          <span className="paymentPremiumCard__icon">
            <FaCrown />
          </span>

          <div>
            <h2>Premium Plan Active</h2>
            <p>All premium dashboard features are already unlocked.</p>
          </div>

          <span className="paymentPremiumCard__badge">
            <FaCheckCircle />
            Active
          </span>
        </div>
      </section>
    );
  }

  return (
    <section className="paymentPage" aria-label="Billing and payments">
      <div className="paymentPage__hero">
        <p className="paymentPage__eyebrow">Billing & Payments</p>
        <h1 className="paymentPage__title">Choose Your Package</h1>
        <p className="paymentPage__subtitle">
          Digital Invitation is required. You can add Guest RSVPs and Liquor Counts,
          then Seating Arrangement.
        </p>
      </div>

      <div className="paymentPage__layout">
        <aside className="paymentPage__sideCard">
          <div className="paymentPage__sideHead">
            <span className="paymentPage__sideIcon">
              <FaCreditCard />
            </span>

            <div>
              <h3>
                {isInstallment
                  ? "Installment Package"
                  : isFullPackage
                  ? "Full Package Offer"
                  : "Selected Package"}
              </h3>

              {!isInstallment && (
                <p>
                  Normal price <del>Rs. {totalNormalPrice.toLocaleString()}</del>
                </p>
              )}

              {isInstallment && <p>All features included with installment payment.</p>}
            </div>
          </div>

          <div className="paymentPage__priceCard">
            <p>{isInstallment ? "Installment Payment Value" : "Payable Now"}</p>
            <h2>Rs. {totalPayable.toLocaleString()}</h2>

            {!isInstallment && isFullPackage && (
              <span>
                Full package offer price: Rs. {FULL_OFFER_PRICE.toLocaleString()}
              </span>
            )}

            {isInstallment && <span>Eligible for 3, 6, and 12 month plans</span>}
          </div>

          <button
            type="button"
            className="paymentPage__payBtn"
            onClick={handlePayNow}
            disabled={isPaying}
          >
            {isPaying ? "Opening Payment..." : "Pay Now"}
          </button>
        </aside>

        <div className="paymentPage__main">
          <div className="paymentPage__panel">
            <div className="paymentPage__panelHead">
              <p>Payment Option</p>
              <h2>Select payment method</h2>
            </div>

            <div className="paymentTabs">
              <button
                type="button"
                className={!isInstallment ? "is-active" : ""}
                onClick={() => handlePaymentTypeChange("card")}
              >
                Direct Card Payment
              </button>

              <button
                type="button"
                className={isInstallment ? "is-active" : ""}
                onClick={() => handlePaymentTypeChange("installment")}
              >
                Installment Payment
              </button>
            </div>

            {isInstallment && (
              <div className="installmentBox">
                <h3>Installment Payment Note</h3>
                <p>
                  If you want to convert your payment into installments, the total
                  value will be Rs. 10,000 and it will enable all features. You can
                  convert it into 3, 6, or 12 month installment plans with HNB,
                  Commercial, and DFCC credit cards.
                </p>

                {/* <div className="installmentMonths">
                  {INSTALLMENT_MONTHS.map((month) => (
                    <button
                      type="button"
                      key={month}
                      className={installmentMonths === month ? "is-active" : ""}
                      onClick={() => setInstallmentMonths(month)}
                    >
                      {month} Months
                    </button>
                  ))}
                </div> */}
              </div>
            )}
          </div>

          <div className="paymentPage__panel">
            <div className="paymentPage__panelHead">
              <p>Package Builder</p>
              <h2>Select your features</h2>
            </div>

            <div className="paymentFeatureCards">
              {FEATURES.map((feature) => {
                const checked = selectedFeatures[feature.key];
                const locked =
                  isInstallment ||
                  feature.key === "digitalInvite" ||
                  (feature.key === "seatingArrangement" && !selectedFeatures.guestRsvp);

                return (
                  <button
                    type="button"
                    key={feature.key}
                    className={`paymentFeatureCard ${
                      checked ? "is-selected" : ""
                    } ${locked ? "is-locked" : ""}`}
                    onClick={() => handleFeatureToggle(feature.key)}
                    disabled={isInstallment}
                  >
                    <span className="paymentFeatureCard__check">
                      {checked && <FaCheckCircle />}
                    </span>

                    <span className="paymentFeatureCard__icon">{feature.icon}</span>

                    <span className="paymentFeatureCard__content">
                      <strong>{feature.title}</strong>
                      <small>
                        {feature.key === "digitalInvite"
                          ? "Required for all packages."
                          : feature.key === "seatingArrangement" &&
                            !selectedFeatures.guestRsvp
                          ? "Available after selecting Guest RSVPs and Liquor Counts."
                          : feature.description}
                      </small>
                    </span>

                    <span className="paymentFeatureCard__price">
                      {!isInstallment && (
                        <del>Rs. {feature.normalPrice.toLocaleString()}</del>
                      )}
                      <b>Rs. {feature.offerPrice.toLocaleString()}</b>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {isFullPackage && (
            <div className="paymentPage__panel paymentBonusPanel">
              <div className="paymentPage__panelHead">
                <p>Full Package Bonus</p>
                <h2>Free with the Rs. 6,990 full package</h2>
              </div>

              <p className="paymentBonusPanel__text">
                When you purchase the full package, you will also receive the Budget
                Management System, QR Code for your digital invitation, Guest Table
                Search, and finalized guest list adding support for free.
              </p>

              <div className="paymentBonusGrid">
                {FREE_BONUSES.map((bonus) => (
                  <div key={bonus.title}>
                    <span>{bonus.icon}</span>
                    <strong>{bonus.title}</strong>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PaymentComponent;